import React from 'react';
import { Box, IconButton } from '@mui/material';
// Deep icon imports: the `@mui/icons-material` barrel drags ~10k CJS modules
// through vitest and stalls every jsdom suite that touches the design system.
import Add from '@mui/icons-material/Add';
import FitScreen from '@mui/icons-material/FitScreen';
import Remove from '@mui/icons-material/Remove';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { KALEIDOSCOPE } from '../../theme/kaleidoscope';
import { DS2_TYPE } from '../v2/tokens';
import { POLISH_GRAPH } from './tokens';
import { safeAlpha } from '../tokens';

/** Floating panel anchored on graph canvases (zoom, expansion, minimap chrome). */
export const graphFloatingPanelSx: SxProps<Theme> = {
  display: 'flex',
  bgcolor: safeAlpha(KALEIDOSCOPE.surface, 0.94),
  backdropFilter: 'blur(12px)',
  border: '1px solid var(--border-default)',
  borderRadius: '12px',
  boxShadow: KALEIDOSCOPE.elevation.md,
  p: 0.5,
  gap: 0.25,
};

/** KG sidebar toolbar section labels */
export const kgToolbarLabelSx: SxProps<Theme> = {
  ...DS2_TYPE.label,
  mb: 1.25,
  display: 'block',
};

/** Comfortable toggle groups for graph toolbars */
export const kgToggleGroupSx: SxProps<Theme> = {
  '& .MuiToggleButton-root': {
    minHeight: 32,
    px: 1.25,
    fontSize: '0.8125rem',
    fontWeight: 500,
    textTransform: 'none',
    border: 'none',
    bgcolor: 'color-mix(in srgb, var(--cockpit-ink) 4%, transparent)',
    '&.Mui-selected': {
      bgcolor: 'var(--selected-bg)',
      color: 'var(--cockpit-ink)',
      '&:hover': { bgcolor: 'var(--selected-bg)' },
    },
    '&:hover': { bgcolor: 'color-mix(in srgb, var(--cockpit-ink) 8%, transparent)' },
  },
  gap: 0.75,
  '& .MuiToggleButtonGroup-grouped': {
    border: 'none !important',
    borderRadius: '8px !important',
    mx: 0.25,
  },
};

export interface GraphZoomControlsProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFit: () => void;
  sx?: SxProps<Theme>;
}

export const GraphZoomControls: React.FC<GraphZoomControlsProps> = ({
  onZoomIn,
  onZoomOut,
  onFit,
  sx,
}) => (
  <Box sx={{ ...graphFloatingPanelSx, ...sx }}>
    <IconButton onClick={onZoomIn} aria-label="Zoom in" sx={{ width: POLISH_GRAPH.controlSize, height: POLISH_GRAPH.controlSize }}>
      <Add sx={{ color: 'var(--cockpit-secondary)' }} />
    </IconButton>
    <IconButton onClick={onZoomOut} aria-label="Zoom out" sx={{ width: POLISH_GRAPH.controlSize, height: POLISH_GRAPH.controlSize }}>
      <Remove sx={{ color: 'var(--cockpit-secondary)' }} />
    </IconButton>
    <IconButton onClick={onFit} aria-label="Fit to view" sx={{ width: POLISH_GRAPH.controlSize, height: POLISH_GRAPH.controlSize }}>
      <FitScreen sx={{ color: 'var(--cockpit-secondary)' }} />
    </IconButton>
  </Box>
);
